import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ClipboardList, Plug, Sparkles, Users, X } from "lucide-react";

type WalkthroughStep = {
  title: string;
  detail: string;
  icon: typeof Sparkles;
  actionLabel?: string;
  onAction?: () => void;
};

export function AgenticOSWalkthrough({
  osId,
  osName,
  accent,
  onOpenMissionControl,
  onOpenIntegrations,
  onOpenOperator,
  onOpenReceipts,
}: {
  osId: string;
  osName: string;
  accent: string;
  onOpenMissionControl?: () => void;
  onOpenIntegrations?: () => void;
  onOpenOperator?: () => void;
  onOpenReceipts?: () => void;
}) {
  const [show, setShow] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const storageKey = `sloe:first-run-walkthrough:${osId}`;

  useEffect(() => {
    if (!osId || typeof window === "undefined") return;
    const showAfterRefresh = window.sessionStorage.getItem("sloe:show-walkthrough-after-refresh") === "1";
    if (showAfterRefresh) {
      window.sessionStorage.removeItem("sloe:show-walkthrough-after-refresh");
      const timer = window.setTimeout(() => setShow(true), 700);
      return () => window.clearTimeout(timer);
    }
    if (window.localStorage.getItem(storageKey) === "done") return;
    const timer = window.setTimeout(() => setShow(true), 900);
    return () => window.clearTimeout(timer);
  }, [osId, storageKey]);

  const steps = useMemo<WalkthroughStep[]>(() => [
    {
      title: `${osName} is an Agentic Operating System`,
      detail: "It is not just a dashboard. Agents, workspace screens, Decisions, Receipts, and permissions work together so the Agentic Operating System can improve around your real work.",
      icon: Sparkles,
      actionLabel: "Open Mission Control",
      onAction: onOpenMissionControl,
    },
    {
      title: "Connect only what you approve",
      detail: "Integrations shows active granted sources only. If an agent needs another source, it asks for the smallest safe permission first.",
      icon: Plug,
      actionLabel: "Open Integrations",
      onAction: onOpenIntegrations,
    },
    {
      title: "Agents can expand by specialty",
      detail: "Operator, Analyst, and Architect are the starting staff. Mission Control can recommend specialist agents for your industry, or research a niche first, and every new agent is staged as a Decision before it joins.",
      icon: Users,
      actionLabel: "Open Mission Control",
      onAction: onOpenMissionControl,
    },
    {
      title: "Agents explain the work",
      detail: "Each agent should show what it can do, what data it needs, what it produced, and why a run failed. If the missing piece is a tool, permission, screen, or skill, it becomes the next clear step.",
      icon: Users,
      actionLabel: "Open Operator",
      onAction: onOpenOperator,
    },
    {
      title: "Receipts prove what changed",
      detail: "Approvals, updates, permission grants, connector repairs, and agent actions should leave a trail so you know what happened and where to look next.",
      icon: ClipboardList,
      actionLabel: "Review Receipts",
      onAction: onOpenReceipts,
    },
  ], [onOpenIntegrations, onOpenMissionControl, onOpenOperator, onOpenReceipts, osName]);

  useEffect(() => {
    if (!show) return;
    steps[stepIndex]?.onAction?.();
  }, [show, stepIndex, steps]);

  if (!show) return null;

  const step = steps[stepIndex];
  const Icon = step.icon;
  const done = stepIndex === steps.length - 1;

  const dismiss = () => {
    window.localStorage.setItem(storageKey, "done");
    setShow(false);
  };

  const action = () => {
    step.onAction?.();
    if (done) dismiss();
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center pointer-events-none px-4 py-6">
      <div className="pointer-events-auto w-full max-w-[460px] overflow-hidden rounded-2xl border border-white/10 bg-[#141414] text-white shadow-2xl">
        <div className="flex items-start gap-4 border-b border-white/10 p-5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: `${accent}22`, color: accent }}>
            <Icon size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-1">
              <span className="rounded-full border border-white/10 bg-white/[0.06] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/55">
                Step {stepIndex + 1} of {steps.length}
              </span>
            </div>
            <h2 className="text-[18px] font-semibold leading-tight">{step.title}</h2>
            <p className="mt-2 text-[13.5px] leading-relaxed text-white/60">{step.detail}</p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss walkthrough"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/40 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex items-center justify-between gap-3 p-5">
          <div className="flex gap-1.5">
            {steps.map((item, index) => (
              <span
                key={item.title}
                className={`h-1.5 rounded-full transition-all ${index === stepIndex ? "w-6" : "w-1.5"}`}
                style={{ background: index === stepIndex ? accent : "rgba(255,255,255,0.18)" }}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {stepIndex > 0 && (
              <button
                type="button"
                onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
                className="rounded-lg border border-white/10 px-3 py-2 text-[12px] font-semibold text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              >
                Back
              </button>
            )}
            {step.actionLabel && (
              <button
                type="button"
                onClick={action}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-[12px] font-semibold text-white/75 transition-colors hover:bg-white/10 hover:text-white"
              >
                <CheckCircle2 size={14} />
                {step.actionLabel}
              </button>
            )}
            <button
              type="button"
              onClick={() => done ? dismiss() : setStepIndex((index) => index + 1)}
              className="rounded-lg px-3.5 py-2 text-[12px] font-semibold text-[#0a0a0a]"
              style={{ background: accent }}
            >
              {done ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
